/**
 * useSidebarCollapsed — Single Source of Truth für den Sidebar-Collapse-State.
 * Persistiert in `localStorage['paypilot.sidebar.collapsed']`.
 *
 * Spec: docs/design-spec.md §2.1.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "paypilot.sidebar.collapsed";

function readInitial(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export interface UseSidebarCollapsedResult {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  toggle: () => void;
}

export function useSidebarCollapsed(): UseSidebarCollapsedResult {
  const [collapsed, setCollapsedState] = useState<boolean>(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      // ignore
    }
  }, [collapsed]);

  // Cross-tab Sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setCollapsedState(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setCollapsed = useCallback((v: boolean) => setCollapsedState(v), []);
  const toggle = useCallback(() => setCollapsedState((v) => !v), []);

  return { collapsed, setCollapsed, toggle };
}
